/**
 * Responsibility: choose a catalogue, and build it when it is not there.
 *
 * Building reads about three hundred set pages and takes minutes, so it
 * is a button rather than something a page visit triggers. The button
 * says which it is doing: an unbuilt catalogue offers "Build", a built
 * one offers "Rebuild" and says how old the prices are.
 *
 * The keyword box shares the filter the arbitrage board uses, because it
 * means the same thing on both - narrow the rows already screened.
 */
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import BuildIcon from '@mui/icons-material/CloudDownloadOutlined';
import AlgorithmIcon from '@mui/icons-material/AutoAwesomeMotion';
import type { CatalogueBoard, CatalogueInfo } from '../../api/client';

type CatalogueControlsProps = {
  catalogues: CatalogueInfo[];
  chosen: string;
  onChosen: (next: string) => void;
  keywords: string;
  onKeywords: (next: string) => void;
  onBuild: () => void;
  building: boolean;
  ready: boolean;
};

export function CatalogueControls({
  catalogues,
  chosen,
  onChosen,
  keywords,
  onKeywords,
  onBuild,
  building,
  ready,
}: CatalogueControlsProps) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <TextField
        select
        size="small"
        label="Catalogue"
        value={chosen}
        onChange={(event) => onChosen(event.target.value)}
        sx={{ minWidth: 150 }}
      >
        {/* The chosen key always has an item, even before the list
            arrives: a select whose value matches nothing renders blank
            and reads as broken on first paint. */}
        {catalogues.length === 0 ? (
          <MenuItem value={chosen}>{chosen}</MenuItem>
        ) : (
          catalogues.map((entry) => (
            <MenuItem key={entry.key} value={entry.key}>
              {entry.label}
              {entry.built ? '' : ' (not built)'}
            </MenuItem>
          ))
        )}
      </TextField>

      <TextField
        size="small"
        label="Keywords"
        placeholder="rookie, topps"
        value={keywords}
        onChange={(event) => onKeywords(event.target.value)}
        sx={{ width: 180 }}
      />

      <span>
        <Button
          size="small"
          variant={ready ? 'outlined' : 'contained'}
          startIcon={<BuildIcon />}
          onClick={onBuild}
          disabled={building}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {building ? 'Reading sets' : ready ? 'Rebuild' : 'Build'}
        </Button>
      </span>

    </Stack>
  );
}

type CatalogueBadgesProps = {
  board: CatalogueBoard | null;
  label: string;
  keywords: string;
  onClearKeywords: () => void;
};

export function CatalogueBadges({
  board,
  label,
  keywords,
  onClearKeywords,
}: CatalogueBadgesProps) {
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
      <Chip
        size="small"
        color="primary"
        icon={<AlgorithmIcon sx={{ fontSize: 15 }} />}
        label="Arbitrage"
      />

      <Chip size="small" variant="outlined" label={label} />

      {board?.ready ? (
        <>
          <Chip
            size="small"
            variant="outlined"
            label={`${board.rows.length}/${board.passed}`}
          />

          <Chip
            size="small"
            variant="outlined"
            label={`${board.universe_size.toLocaleString()} cards screened`}
          />

        </>
      ) : (
        <Chip size="small" variant="outlined" color="warning" label="not built yet" />
      )}

      {/* Said plainly, because the Pokemon board beside this one does have
          a gem rate and the two numbers are different claims. */}
      <Chip size="small" variant="outlined" color="warning" label="best case, no gem rate" />

      {keywords.trim() ? (
        <Chip
          size="small"
          color="secondary"
          label={`"${keywords.trim()}"`}
          onDelete={onClearKeywords}
        />
      ) : null}
    </Stack>
  );
}
